import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface DoodleStroke {
  c: string;
  w: number;
  pts: number[];
}

const COLORS = ['#111111', '#db2777', '#1d63d1', '#16a34a', '#ea580c', '#ffffff'];
const WIDTHS = [3, 6, 10];

interface DrawingCanvasProps {
  disabled?: boolean;
  onSubmit: (strokes: DoodleStroke[]) => void;
}

/** Phone drawing pad — coords normalized to 0..1000 for TV replay. */
export const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ disabled, onSubmit }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<DoodleStroke[]>([]);
  const drawingRef = useRef(false);
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(WIDTHS[1]);
  const [strokeCount, setStrokeCount] = useState(0);

  const redraw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const S = Math.min(cv.width, cv.height) / 1000;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    for (const st of strokesRef.current) {
      drawStroke(ctx, st, S);
    }
  }, []);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const parent = cv.parentElement;
    const size = Math.min(parent?.clientWidth || 320, 360);
    cv.width = size;
    cv.height = size;
    redraw();
  }, [redraw]);

  const toNorm = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current!;
    const rect = cv.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 1000;
    const y = ((e.clientY - rect.top) / rect.height) * 1000;
    return [x, y];
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const [x, y] = toNorm(e);
    strokesRef.current.push({ c: color, w: width, pts: [x, y, x, y] });
    setStrokeCount(strokesRef.current.length);
    redraw();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    const st = strokesRef.current[strokesRef.current.length - 1];
    if (!st) return;
    const [x, y] = toNorm(e);
    st.pts.push(x, y);
    redraw();
  };

  const onPointerUp = () => {
    drawingRef.current = false;
  };

  const undo = () => {
    strokesRef.current.pop();
    setStrokeCount(strokesRef.current.length);
    redraw();
  };

  const clear = () => {
    strokesRef.current = [];
    setStrokeCount(0);
    redraw();
  };

  return (
    <div className="doodle-draw-pad">
      <canvas
        ref={canvasRef}
        className="doodle-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div className="doodle-draw-tools">
        <div className="doodle-colors">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`doodle-swatch${color === c ? ' active' : ''}`}
              style={{ background: c, borderColor: c === '#ffffff' ? '#ccc' : c }}
              onClick={() => setColor(c)}
              disabled={disabled}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
        <div className="doodle-widths">
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              className={`doodle-width${width === w ? ' active' : ''}`}
              onClick={() => setWidth(w)}
              disabled={disabled}
            >
              {w}
            </button>
          ))}
        </div>
        <div className="doodle-draw-actions">
          <button type="button" onClick={undo} disabled={disabled || strokeCount === 0}>
            Undo
          </button>
          <button type="button" onClick={clear} disabled={disabled || strokeCount === 0}>
            Clear
          </button>
          <button
            type="button"
            className="doodle-submit"
            onClick={() => onSubmit(strokesRef.current.slice())}
            disabled={disabled || strokeCount === 0}
          >
            Submit drawing
          </button>
        </div>
      </div>
    </div>
  );
};

interface StrokeReplayProps {
  strokes: DoodleStroke[];
  className?: string;
  size?: number;
}

export const StrokeReplay: React.FC<StrokeReplayProps> = ({ strokes, className, size = 420 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    cv.width = size;
    cv.height = size;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const S = size / 1000;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    for (const st of strokes || []) {
      drawStroke(ctx, st, S);
    }
  }, [strokes, size]);

  return <canvas ref={canvasRef} className={className || 'doodle-replay'} width={size} height={size} />;
};

function drawStroke(ctx: CanvasRenderingContext2D, st: DoodleStroke, S: number) {
  const pts = st.pts;
  if (!pts || pts.length < 4) return;
  ctx.strokeStyle = st.c || '#111';
  ctx.lineWidth = (st.w || 4) * S;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(pts[0] * S, pts[1] * S);
  for (let i = 2; i < pts.length; i += 2) {
    ctx.lineTo(pts[i] * S, pts[i + 1] * S);
  }
  ctx.stroke();
}
